import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import {
  Description as DocumentationIcon,
  Notes as ReleaseNotesIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  DataUsage as DataCollectorIcon,
  Analytics as OrderAnalyzerIcon,
  Assessment as VendorScorecardIcon,
  Timeline as ChartIcon,
  TableChart as ExcelIcon,
  TrendingUp as OptimizationIcon,
  MenuBook as MenuBookIcon,
} from "@mui/icons-material";
import ReleaseNotesDialog from "./ReleaseNotesDialog";

interface HeaderProps {
  activeTab?: number;
  onTabChange?: (event: React.SyntheticEvent, newValue: number) => void;
  onRefresh?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  activeTab = 0,
  onTabChange,
  onRefresh,
}) => {
  const { logout } = useAuth();
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      logout();
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Top Navigation Bar */}
      <AppBar
        position="static"
        sx={{
          backgroundColor: "#e9ecef",
          boxShadow: "none",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
          {/* Left side - Site name */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1976d2",
                fontWeight: "bold",
                fontSize: "1.1rem",
              }}
            >
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", mr: 0.5 }}
              >
                <ChartIcon sx={{ fontSize: "1.15rem", mr: 0.5 }} />
              </Box>
              Sears K<em>AI</em>ros Command Center
            </Typography>
          </Box>

          {/* Right side - Action buttons and timestamp */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DocumentationIcon />}
              disabled={true}
              onClick={() => window.open("/documentation.html", "_blank")}
              sx={{
                color: "#00bcd4",
                borderColor: "#00bcd4",
                textTransform: "none",
                fontSize: "0.75rem",
                minWidth: "auto",
                px: 1.5,
                py: 0.5,
                "&:hover": {
                  borderColor: "#00acc1",
                  backgroundColor: "rgba(0, 188, 212, 0.04)",
                },
              }}
            >
              Documentation
            </Button>

            {/* <Button
              variant="outlined"
              size="small"
              startIcon={<WhatsNewIcon />}
              disabled={true}
              sx={{
                color: "#4caf50",
                borderColor: "#4caf50",
                textTransform: "none",
                fontSize: "0.75rem",
                minWidth: "auto",
                px: 1.5,
                py: 0.5,
                "&:hover": {
                  borderColor: "#43a047",
                  backgroundColor: "rgba(76, 175, 80, 0.04)",
                },
              }}
            >
              What's New
            </Button> */}

            <Button
              variant="outlined"
              size="small"
              startIcon={<ReleaseNotesIcon />}
              disabled={true}
              onClick={() => setReleaseNotesOpen(true)}
              sx={{
                color: "#2196f3",
                borderColor: "#2196f3",
                textTransform: "none",
                fontSize: "0.75rem",
                minWidth: "auto",
                px: 1.5,
                py: 0.5,
                "&:hover": {
                  borderColor: "#1976d2",
                  backgroundColor: "rgba(33, 150, 243, 0.04)",
                },
              }}
            >
              Release Notes
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              disabled={true}
              sx={{
                color: "#2196f3",
                borderColor: "#2196f3",
                textTransform: "none",
                fontSize: "0.75rem",
                minWidth: "auto",
                px: 1.5,
                py: 0.5,
                "&:hover": {
                  borderColor: "#1976d2",
                  backgroundColor: "rgba(33, 150, 243, 0.04)",
                },
              }}
              onClick={onRefresh}
            >
              Refresh
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                color: "#f44336",
                borderColor: "#f44336",
                textTransform: "none",
                fontSize: "0.75rem",
                minWidth: "auto",
                px: 1.5,
                py: 0.5,
                "&:hover": {
                  borderColor: "#d32f2f",
                  backgroundColor: "rgba(244, 67, 54, 0.04)",
                },
              }}
            >
              Logout
            </Button>

            {/* <Typography
              variant="caption"
              sx={{
                color: "#666",
                fontSize: "0.75rem",
                ml: 2,
              }}
            >
              Updated hourly (CT): 02/04/2026
            </Typography> */}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Secondary Navigation - Home Tab */}
      {/* <AppBar
        position="static"
        sx={{
          backgroundColor: "white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar
          sx={{
            minHeight: "48px !important",
            px: 2,
            justifyContent: "space-between",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={onTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: "48px",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#666",
                "&.Mui-selected": {
                  color: "#1976d2",
                  fontWeight: 600,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1976d2",
                height: 3,
              },
            }}
          >
            <Tab
              icon={<ExcelIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="By Hour"
              sx={{
                "& .MuiTab-iconWrapper": {
                  marginRight: 1,
                  marginBottom: "0 !important",
                },
              }}
            />
          </Tabs>
        </Toolbar>
      </AppBar> */}

      {/* Release Notes Dialog */}
      <ReleaseNotesDialog
        open={releaseNotesOpen}
        onClose={() => setReleaseNotesOpen(false)}
      />
    </Box>
  );
};

export default Header;
